import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

export default function EcoFlowBanner() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      const body = new URLSearchParams();
      body.append("email", email.trim());
      body.append("brand", "EcoFlow");
      body.append("source", "Homepage — EcoFlow Coming Soon Banner");
      const res = await fetch("https://readdy.ai/api/form/d77qgkadqn9mib2tqc3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="py-6 px-4 md:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="relative rounded-2xl overflow-hidden bg-gray-950">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/19cfdd845ede72df278dd6e0ddb693f2.jpg"
              alt="EcoFlow portable power stations coming soon to Iron Haven Supply"
              className="w-full h-full object-cover object-center opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/90 to-gray-950/60" />
          </div>

          {/* Decorative glow */}
          <div
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 75% 50%, #f59e0b 0%, transparent 55%)",
            }}
          />

          <div className="relative px-6 md:px-10 py-10 flex flex-col lg:flex-row items-center gap-8">
            {/* Left — copy */}
            <div className="flex-1 min-w-0">
              {/* Badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold px-3 py-1 rounded-full">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  Coming Soon
                </span>
                <span className="text-gray-600 text-xs font-medium">Onboarding in progress</span>
              </div>

              <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight leading-tight mb-2">
                EcoFlow is Coming to<br className="hidden md:block" /> Iron Haven Supply
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed max-w-lg mb-5">
                We&apos;re finalizing our authorized reseller agreement with EcoFlow — home of the DELTA &amp; RIVER series. Be the first to know when they go live, and get early access to any launch deals.
              </p>

              {/* Product teaser chips */}
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  { icon: "ri-flashlight-line", label: "DELTA 2", sub: "1024Wh" },
                  { icon: "ri-battery-charge-line", label: "DELTA Pro", sub: "3600Wh" },
                  { icon: "ri-drop-line", label: "RIVER 2 Pro", sub: "768Wh" },
                  { icon: "ri-home-wifi-line", label: "Smart Home Panel", sub: "10 circuits" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${item.icon} text-amber-400 text-xs`}></i>
                    </div>
                    <span className="text-white text-xs font-semibold">{item.label}</span>
                    <span className="text-gray-500 text-xs">{item.sub}</span>
                  </div>
                ))}
              </div>

              {/* Signup form */}
              {status === "success" ? (
                <div className="flex items-center gap-3 bg-emerald-500/15 border border-emerald-500/25 rounded-xl px-5 py-3.5 max-w-md">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/25 rounded-full shrink-0">
                    <i className="ri-check-line text-emerald-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-emerald-300 font-bold text-sm">You&apos;re on the list!</p>
                    <p className="text-emerald-400/70 text-xs mt-0.5">
                      We&apos;ll email you the moment EcoFlow launches on Iron Haven Supply.
                    </p>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="ecoflow-homepage-notify"
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-2.5 max-w-md"
                >
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-amber-400/60 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0"
                  >
                    {status === "submitting" ? (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin text-sm"></i>
                        </div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-bell-line text-sm"></i>
                        </div>
                        Notify Me
                      </>
                    )}
                  </button>
                </form>
              )}

              {status === "error" && (
                <p className="text-red-400 text-xs mt-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  Something went wrong. Please try again or{" "}
                  <Link to="/contact" className="underline">contact us</Link>.
                </p>
              )}

              {status !== "success" && (
                <p className="text-gray-600 text-xs mt-2">
                  No spam — just one email when EcoFlow goes live.
                </p>
              )}
            </div>

            {/* Right — stats + CTA */}
            <div className="shrink-0 flex flex-col items-center lg:items-end gap-4 lg:text-right">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "3,000+", label: "Battery cycles (LFP)" },
                  { value: "0–80%", label: "Charge in 50 min" },
                  { value: "25kWh", label: "Max expandable cap." },
                  { value: "2-yr", label: "Manufacturer warranty" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center"
                  >
                    <p className="text-white font-extrabold text-lg leading-none">{s.value}</p>
                    <p className="text-gray-500 text-[10px] mt-1 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Learn more link */}
              <Link
                to="/authorized-brands"
                onClick={() => {
                  // hint to the brands page to open EcoFlow tab
                  sessionStorage.setItem("brands-active", "ecoflow");
                }}
                className="flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                See full EcoFlow lineup
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
